// The two languages, where a choice is kept on this device, and what the app
// starts in. Pure: every browser API is passed in, so this runs in Node tests.

export const LANGUAGES = ["bs", "en"];
export const DEFAULT_LANGUAGE = "bs";
export const STORAGE_KEY = "dekamas-lang";
export const HINT_DISMISSED_KEY = "dekamas-lang-hint-dismissed";

// Each language named in itself, so someone who cannot read the current one can
// still find theirs.
export const LANGUAGE_NAMES = { bs: "Bosanski", en: "English" };

// Everyone who reads the Bosnian site: Bosnian, Croatian, Serbian,
// Serbo-Croatian, Montenegrin.
const BCMS = ["bs", "hr", "sr", "sh", "cnr"];

export function isSupportedLanguage(value) {
	return LANGUAGES.includes(value);
}

export function isEnglishEnabled(env) {
	return env?.VITE_ENABLE_ENGLISH === "true";
}

// Blocked storage throws on access. The language then lives in memory for the
// visit: it applies, it just isn't remembered.
function safeGet(storage, key) {
	try {
		return storage?.getItem(key) ?? null;
	} catch {
		return null;
	}
}

function safeSet(storage, key, value) {
	try {
		storage?.setItem(key, value);
	} catch {
		// The choice still applies for this visit.
	}
}

export function readStoredLanguage(storage) {
	const value = safeGet(storage, STORAGE_KEY);
	return isSupportedLanguage(value) ? value : null;
}

export function writeStoredLanguage(storage, language) {
	if (isSupportedLanguage(language)) safeSet(storage, STORAGE_KEY, language);
}

export function isHintDismissed(storage) {
	return safeGet(storage, HINT_DISMISSED_KEY) === "1";
}

export function dismissHint(storage) {
	safeSet(storage, HINT_DISMISSED_KEY, "1");
}

// Never writes: a first-time visitor has not chosen Bosnian, they just have
// not chosen yet — and only they can be offered the English hint.
export function resolveInitialLanguage({ storage, enabled }) {
	if (!enabled) return DEFAULT_LANGUAGE;
	// Future SEO phase: an /en/ path prefix decides before anything else.
	return readStoredLanguage(storage) ?? DEFAULT_LANGUAGE;
}

function primarySubtag(tag) {
	return String(tag).toLowerCase().split(/[-_]/)[0];
}

// Many local users run English phones and browsers but also list Croatian,
// Serbian or Bosnian; they read the Bosnian site and must not be nagged.
export function shouldShowEnglishHint({ enabled, storage, hasToken, languages }) {
	if (!enabled || hasToken) return false;
	if (readStoredLanguage(storage) || isHintDismissed(storage)) return false;

	const list = Array.from(languages ?? []);
	if (list.length === 0 || primarySubtag(list[0]) !== "en") return false;

	return !list.some((tag) => BCMS.includes(primarySubtag(tag)));
}

// Which language to apply after whoami. The account decides — on every login,
// on every device — but only when the signed-in user changes (first load with a
// token, login, signup, another account). A refetch of the same user must
// never undo a switch, and neither may a response that raced a save still in
// flight: that case keeps the previous key so it is checked again afterwards.
//
// `remember` is the account's language to write to the device on that same
// change, whether or not it differs from what is showing: the account's
// language is the device's choice too, so after logging out a Bosnian account
// on a fresh browser is not mistaken for a first-time visitor.
export function accountLanguageToApply({ previousUserKey, user, savePending, current }) {
	if (savePending) return { userKey: previousUserKey, language: null, remember: null };

	const userKey = user?.email ?? null;
	if (!user || userKey === previousUserKey) return { userKey, language: null, remember: null };

	const remember = isSupportedLanguage(user.language) ? user.language : null;
	return { userKey, language: remember !== current ? remember : null, remember };
}

// The whoami cache after a successful save, with the new language stamped on
// the account that switched. Someone else may have signed in while the save
// was in flight; they must not inherit it.
export function withAccountLanguage(whoami, email, language) {
	if (!email || whoami?.user?.email !== email) return whoami;
	return { ...whoami, user: { ...whoami.user, language } };
}
